import React, { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

const SubscriptionEnforcement: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [extendDays, setExtendDays] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const token = localStorage.getItem("token");

  const load = async () => {
    try {
      setError("");
      const res = await axios.get(getApiUrl("superadmin/subscription-enforcement"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRows(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load subscription enforcement.");
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activate = async (id: number) => {
    await axios.post(getApiUrl(`superadmin/subscription-enforcement/${id}/activate`), {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    load();
  };

  const suspend = async (id: number) => {
    await axios.post(getApiUrl(`superadmin/subscription-enforcement/${id}/suspend`), {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    load();
  };

  const exempt = async (id: number, value: boolean) => {
    await axios.post(getApiUrl(`superadmin/subscription-enforcement/${id}/exempt`), { exempt: value }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    load();
  };

  const extend = async (id: number) => {
    const days = Number(extendDays[String(id)] || 0);
    if (!days) return;
    await axios.post(getApiUrl(`superadmin/subscription-enforcement/${id}/extend`), { days }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Subscription Enforcement</h1>
        {error && (
          <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
            {error}
          </div>
        )}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50/60">
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">School</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Plan</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Expiry</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Exempt</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/40">
                  <td className="px-6 py-4 font-semibold text-slate-700">{r.name}</td>
                  <td className="px-6 py-4">{r.status}</td>
                  <td className="px-6 py-4">{r.subscription_tier || r.plan_type || "-"}</td>
                  <td className="px-6 py-4">{r.current_period_end || "-"}</td>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={!!r.is_subscription_exempt}
                      onChange={(e) => exempt(r.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex flex-wrap gap-2 justify-end">
                      <button
                        onClick={() => activate(r.id)}
                        disabled={String(r.status || "").toLowerCase() === "active"}
                        className={`px-3 py-1.5 text-xs rounded-xl ${
                          String(r.status || "").toLowerCase() === "active"
                            ? "bg-emerald-50 text-emerald-700 cursor-default"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {String(r.status || "").toLowerCase() === "active" ? "Active" : "Activate"}
                      </button>
                      <button
                        onClick={() => suspend(r.id)}
                        disabled={String(r.status || "").toLowerCase() === "suspended"}
                        className={`px-3 py-1.5 text-xs rounded-xl ${
                          String(r.status || "").toLowerCase() === "suspended"
                            ? "bg-rose-50 text-rose-700 cursor-default"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {String(r.status || "").toLowerCase() === "suspended" ? "Suspended" : "Suspend"}
                      </button>
                      <div className="flex items-center gap-1">
                        <input
                          className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-xs"
                          placeholder="Days"
                          value={extendDays[String(r.id)] || ""}
                          onChange={(e) => setExtendDays(prev => ({ ...prev, [String(r.id)]: e.target.value }))}
                        />
                        <button onClick={() => extend(r.id)} className="px-3 py-1.5 text-xs rounded-xl bg-slate-100">Extend</button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-6 text-slate-500">No schools found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionEnforcement;
